import { generate } from "@suaveplan/qrcode";

export const QR_STUDIO_DEFAULT_VALUE = "https://qrcode.suaveplan.com";

export type ErrorCorrectionLevel = "L" | "M" | "Q" | "H";

export interface QrStudioSettings {
    readonly background: string;
    readonly color: string;
    readonly errorCorrectionLevel: ErrorCorrectionLevel;
    readonly scale: number;
    readonly value: string;
}

export const QR_STUDIO_DEFAULTS: QrStudioSettings = {
    background: "#ffffff",
    color: "#0d0f0d",
    errorCorrectionLevel: "M",
    scale: 10,
    value: QR_STUDIO_DEFAULT_VALUE,
};

export function createQrCode(settings: QrStudioSettings) {
    return generate(settings.value.trim(), {
        background: settings.background,
        color: settings.color,
        errorCorrectionLevel: settings.errorCorrectionLevel,
        margin: 4,
        scale: settings.scale,
    });
}

export function svgBlob(svg: string): Blob {
    return new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
}

export function downloadBlob(blob: Blob, filename: string): void {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function pngBlob(svg: string, size: number): Promise<Blob> {
    const image = new Image();
    const url = URL.createObjectURL(svgBlob(svg));
    try {
        await new Promise<void>((resolve, reject) => {
            image.onload = () => resolve();
            image.onerror = () =>
                reject(new Error("QR image could not render"));
            image.src = url;
        });
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext("2d");
        if (context === null) throw new Error("Canvas is unavailable");
        context.imageSmoothingEnabled = false;
        context.drawImage(image, 0, 0, size, size);
        return await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (blob) =>
                    blob === null
                        ? reject(new Error("PNG export could not complete"))
                        : resolve(blob),
                "image/png"
            );
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}
